import { useEffect, useState } from 'react';
import { supabase } from '@shared/lib/supabase';
import DateRangeSelector, { getFilterDates } from '@dashboard/shared/DateRangeSelector';
import type { DateFilter } from '@dashboard/shared/DateRangeSelector';

interface RevenueSummary {
  totalVolume: number;
  platformFees: number;
  completedOrders: number;
  activeMerchants: number;
  avgOrderValue: number;
}

interface DailyData {
  date: string;
  volume: number;
  fees: number;
  orders: number;
}

const PLATFORM_FEE_RATE = 0.025;

function StatCard({ label, value, sub, highlight }: { label: string; value: string; sub?: string; highlight?: boolean }) {
  return (
    <div
      className="rounded p-4 transition-colors"
      style={{
        border: highlight ? '1px solid rgba(16,185,129,0.2)' : '1px solid var(--dash-border)',
        background: highlight ? 'rgba(16,185,129,0.05)' : 'var(--dash-card)',
      }}
      onMouseEnter={(e) => { if (!highlight) e.currentTarget.style.background = 'var(--dash-card-hover)'; }}
      onMouseLeave={(e) => { if (!highlight) e.currentTarget.style.background = highlight ? 'rgba(16,185,129,0.05)' : 'var(--dash-card)'; }}
    >
      <div
        className="font-mono text-[10px] uppercase tracking-widest mb-1.5"
        style={{ color: highlight ? undefined : 'var(--dash-text-dim)' }}
      >
        {highlight ? <span className="text-emerald-400">{label}</span> : label}
      </div>
      <div
        className="font-mono text-xl"
        style={{ color: highlight ? undefined : 'var(--dash-text-primary)' }}
      >
        {highlight ? <span className="text-emerald-300">{value}</span> : value}
      </div>
      {sub && <p className="font-mono text-[10px] mt-1" style={{ color: 'var(--dash-text-dim)' }}>{sub}</p>}
    </div>
  );
}

export default function AdminRevenue() {
  const [summary, setSummary] = useState<RevenueSummary>({
    totalVolume: 0,
    platformFees: 0,
    completedOrders: 0,
    activeMerchants: 0,
    avgOrderValue: 0,
  });
  const [dailyData, setDailyData] = useState<DailyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>({ range: '30d' });

  useEffect(() => {
    loadData();
  }, [dateFilter]);

  const loadData = async () => {
    setLoading(true);
    const { start, end } = getFilterDates(dateFilter);

    let ordersQuery = supabase
      .from('orders')
      .select('id, verified_amount, expected_amount, status, created_at, merchant_id')
      .eq('status', 'completed');

    if (start) ordersQuery = ordersQuery.gte('created_at', start);
    if (end) ordersQuery = ordersQuery.lte('created_at', end);

    const [ordersRes, merchantsRes] = await Promise.all([
      ordersQuery,
      supabase.from('merchants').select('id', { count: 'exact', head: true }).eq('is_active', true),
    ]);

    const completedOrders = ordersRes.data || [];
    const totalVolume = completedOrders.reduce(
      (sum, o) => sum + (Number(o.verified_amount) || Number(o.expected_amount) || 0),
      0,
    );
    const platformFees = totalVolume * PLATFORM_FEE_RATE;
    const avgOrderValue = completedOrders.length > 0 ? totalVolume / completedOrders.length : 0;

    setSummary({
      totalVolume,
      platformFees,
      completedOrders: completedOrders.length,
      activeMerchants: merchantsRes.count || 0,
      avgOrderValue,
    });

    const grouped = completedOrders.reduce<Record<string, { volume: number; fees: number; orders: number }>>(
      (acc, o) => {
        const date = new Date(o.created_at).toISOString().slice(0, 10);
        if (!acc[date]) acc[date] = { volume: 0, fees: 0, orders: 0 };
        const amt = Number(o.verified_amount) || Number(o.expected_amount) || 0;
        acc[date].volume += amt;
        acc[date].fees += amt * PLATFORM_FEE_RATE;
        acc[date].orders += 1;
        return acc;
      },
      {},
    );

    const sorted = Object.entries(grouped)
      .map(([date, d]) => ({ date, ...d }))
      .sort((a, b) => a.date.localeCompare(b.date));

    const sliceCount = dateFilter.range === '7d' ? 7 : dateFilter.range === '30d' ? 30 : dateFilter.range === '90d' ? 90 : sorted.length;
    setDailyData(sorted.slice(-sliceCount));
    setLoading(false);
  };

  const fmt = (n: number) =>
    `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <p className="font-mono text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
      </div>
    );
  }

  const maxVolume = Math.max(...dailyData.map((d) => d.volume), 1);
  const topDay = dailyData.reduce((best, d) => (d.volume > best.volume ? d : best), dailyData[0] || { date: '--', volume: 0 });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>Platform Revenue</h1>
        <DateRangeSelector value={dateFilter} onChange={setDateFilter} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total Volume" value={fmt(summary.totalVolume)} />
        <StatCard
          label="Platform Fees"
          value={fmt(summary.platformFees)}
          sub={`${(PLATFORM_FEE_RATE * 100).toFixed(1)}% fee rate`}
          highlight
        />
        <StatCard label="Completed Orders" value={String(summary.completedOrders)} />
        <StatCard label="Avg Order Value" value={fmt(summary.avgOrderValue)} />
        <StatCard
          label="Top Day"
          value={topDay ? fmt(topDay.volume) : '--'}
          sub={topDay?.date || ''}
        />
      </div>

      <div
        className="rounded p-5"
        style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-dim)' }}>
            Daily Volume
          </h2>
          <span className="font-mono text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>
            {dailyData.length} day{dailyData.length !== 1 ? 's' : ''}
          </span>
        </div>

        {dailyData.length === 0 ? (
          <div className="py-12 text-center">
            <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>No completed orders to display.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {dailyData.map((d) => (
              <div key={d.date} className="flex items-center gap-4">
                <span className="font-mono text-[10px] w-20 shrink-0 text-right" style={{ color: 'var(--dash-text-dim)' }}>
                  {d.date}
                </span>
                <div
                  className="flex-1 h-6 rounded overflow-hidden relative"
                  style={{ background: 'var(--dash-hover-bg)' }}
                >
                  <div
                    className={`h-full rounded transition-all duration-300 ${
                      d === topDay ? 'bg-emerald-500/30' : ''
                    }`}
                    style={{
                      width: `${(d.volume / maxVolume) * 100}%`,
                      background: d === topDay ? undefined : 'var(--dash-card-hover)',
                    }}
                  />
                  <div className="absolute inset-0 flex items-center px-3">
                    <span className="font-mono text-[10px]" style={{ color: 'var(--dash-text-muted)' }}>
                      {fmt(d.volume)} ({d.orders} orders)
                    </span>
                  </div>
                </div>
                <span className="font-mono text-[10px] w-20 shrink-0" style={{ color: 'var(--dash-text-dim)' }}>
                  fee: {fmt(d.fees)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
